import { useState } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { useClientService, CreateClientPayload, CreateClientResult } from '../services/clientService'
import { MatterType } from '../services/matterService'
import { useTerminology } from '../contexts/TerminologyContext'

interface CreateClientFormProps {
  onCreated: (result: CreateClientResult) => void
}

const matterTypes: MatterType[] = ['Sale', 'Purchase', 'Transfer', 'Subdivision']

interface FormState {
  firstName: string
  lastName: string
  emailAddress: string
  mobileNumber: string
  companyName: string
  streetAddress: string
  suburb: string
  state: string
  postcode: string
  country: string
  bankAccountName: string
  bankAccountBsb: string
  bankAccountNumber: string
  openMatter: boolean
  matterType: MatterType
  matterPropertyAddress: string
  settlementDate: string
}

const firstNames = [
  'Olivia', 'Liam', 'Emma', 'Noah', 'Ava', 'William', 'Sophia', 'James',
  'Isabella', 'Benjamin', 'Mia', 'Lucas', 'Charlotte', 'Henry', 'Amelia',
  'Alexander', 'Harper', 'Michael', 'Evelyn', 'Ethan',
]

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Wilson', 'Anderson', 'Taylor', 'Thomas',
  'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
]

const streetNames = ['High St', 'Chapel St', 'Church St', 'Station Rd', 'Victoria Ave', 'Beach Rd', 'Park St', 'King St']
const suburbs = ['Melbourne', 'Richmond', 'St Kilda', 'Fitzroy', 'Brunswick', 'South Yarra', 'Carlton', 'Prahran']

const randomFrom = (values: string[]) => values[Math.floor(Math.random() * values.length)]

const randomPropertyAddress = () => `${Math.floor(Math.random() * 200) + 1} ${randomFrom(streetNames)}, ${randomFrom(suburbs)} VIC`

const defaultSettlementDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 60)
  return d.toISOString().slice(0, 10)
}

const createInitialState = (): FormState => {
  const firstName = randomFrom(firstNames)
  const lastName = randomFrom(lastNames)
  return {
    firstName,
    lastName,
    emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    mobileNumber: '0400000000',
    companyName: '',
    streetAddress: '1 Example Street',
    suburb: 'Melbourne',
    state: 'VIC',
    postcode: '3000',
    country: 'AU',
    bankAccountName: `${firstName} ${lastName}`,
    bankAccountBsb: '062100',
    bankAccountNumber: '56781234',
    openMatter: false,
    matterType: 'Sale',
    matterPropertyAddress: randomPropertyAddress(),
    settlementDate: defaultSettlementDate(),
  }
}

export const CreateClientForm: React.FC<CreateClientFormProps> = ({ onCreated }) => {
  const terminology = useTerminology()
  const [form, setForm] = useState<FormState>(createInitialState)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { createClient } = useClientService()

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required')
      return
    }

    if (form.openMatter && !form.matterPropertyAddress.trim()) {
      setError(`Property address is required to open a ${terminology.matter.toLowerCase()}`)
      return
    }

    const payload: CreateClientPayload = {
      firstName: form.firstName,
      lastName: form.lastName,
      emailAddress: form.emailAddress,
      mobileNumber: form.mobileNumber,
      companyName: form.companyName,
      streetAddress: form.streetAddress,
      suburb: form.suburb,
      state: form.state,
      postcode: form.postcode,
      country: form.country,
      source: {
        sourceType: 'bank-account',
        bankAccountName: form.bankAccountName,
        bankAccountBsb: form.bankAccountBsb,
        bankAccountNumber: form.bankAccountNumber,
      },
      matter: form.openMatter ? {
        matterType: form.matterType,
        propertyAddress: form.matterPropertyAddress,
        settlementDate: form.settlementDate,
      } : undefined,
    }

    setLoading(true)
    try {
      const result = await createClient(payload)
      onCreated(result)
      setForm(createInitialState())
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${terminology.client.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <Alert type="error">{error}</Alert>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="First Name" required value={form.firstName} onChange={handleChange('firstName')} />
        <Input label="Last Name" required value={form.lastName} onChange={handleChange('lastName')} />
        <Input label="Email" type="email" value={form.emailAddress} onChange={handleChange('emailAddress')} />
        <Input label="Mobile Number" value={form.mobileNumber} onChange={handleChange('mobileNumber')} />
        <Input label="Company Name" value={form.companyName} onChange={handleChange('companyName')} />
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Street Address" value={form.streetAddress} onChange={handleChange('streetAddress')} />
          <Input label="Suburb" value={form.suburb} onChange={handleChange('suburb')} />
          <Input label="State" value={form.state} onChange={handleChange('state')} />
          <Input label="Postcode" value={form.postcode} onChange={handleChange('postcode')} />
          <Input label="Country" value={form.country} onChange={handleChange('country')} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Bank Account (Payment Source)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Account Name" value={form.bankAccountName} onChange={handleChange('bankAccountName')} />
          <Input label="BSB" value={form.bankAccountBsb} onChange={handleChange('bankAccountBsb')} />
          <Input label="Account Number" value={form.bankAccountNumber} onChange={handleChange('bankAccountNumber')} />
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
          <input
            type="checkbox"
            checked={form.openMatter}
            onChange={(e) => setForm((prev) => ({ ...prev, openMatter: e.target.checked }))}
          />
          Also open a {terminology.matter.toLowerCase()} for this {terminology.client.toLowerCase()}
        </label>

        {form.openMatter && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">{terminology.matter} Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                value={form.matterType}
                onChange={(e) => setForm((prev) => ({ ...prev, matterType: e.target.value as MatterType }))}
              >
                {matterTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Input label="Settlement Date" type="date" value={form.settlementDate} onChange={handleChange('settlementDate')} />
            <div className="md:col-span-2">
              <Input label="Property Address" value={form.matterPropertyAddress} onChange={handleChange('matterPropertyAddress')} />
            </div>
          </div>
        )}
      </div>

      <Button type="submit" disabled={loading}>
        {loading ? `Creating ${terminology.client.toLowerCase()}...` : `Create ${terminology.client}`}
      </Button>
    </form>
  )
}
